import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteConfig } from "@/contexts/SiteConfigContext";
import ChangePasswordCard from "./ChangePasswordCard";

export default function MySettings() {
  const site = useSiteConfig();
  const { refresh: refreshAuth } = useAuth();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  const [watermarkText, setWatermarkText] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("gbp");

  const load = async () => {
    try {
      const { data } = await api.get("/photographer/me");
      setMe(data);
      setWatermarkText(data.watermark_text || "");
      setCompanyName(data.company_name || "");
      setPhone(data.phone || "");
      setWebsite(data.website || "");
      setDefaultCurrency((data.default_currency || "gbp").toLowerCase());
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/photographer/me", {
        watermark_text: watermarkText,
        company_name: companyName,
        phone, website,
        default_currency: defaultCurrency,
      });
      toast.success("Profile saved");
      await refreshAuth();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!me) return <p className="text-white/50 text-sm">Loading…</p>;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-testid="my-settings">
      <form onSubmit={save} className="space-y-5">
        <h3 className="font-display text-3xl uppercase">My profile</h3>
        <p className="text-sm text-white/50 font-condensed">
          Your company name appears on every photo watermark. `{site.site_domain}` is always added in the corner to protect the site&apos;s brand too.
        </p>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Company / brand name</label>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="input-minimal" data-testid="my-company" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Watermark text</label>
          <input value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)} placeholder="Defaults to your company name" className="input-minimal" data-testid="my-watermark" />
          <p className="text-xs text-white/40 mt-1 font-condensed">Tiled diagonally across every preview at low opacity. Only newly uploaded photos use the updated text.</p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1 font-semibold">Phone</label>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input-minimal" data-testid="my-phone" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-white/40 block mb-1 font-semibold">Website</label>
            <input value={website} onChange={(e) => setWebsite(e.target.value)} className="input-minimal" data-testid="my-website" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] pm-orange block mb-1 font-semibold">Default currency</label>
          <select
            value={defaultCurrency}
            onChange={(e) => setDefaultCurrency(e.target.value)}
            className="input-minimal bg-black"
            data-testid="my-default-currency"
          >
            <option value="gbp">GBP · £ (Pound Sterling)</option>
            <option value="usd">USD · $ (US Dollar)</option>
            <option value="eur">EUR · € (Euro)</option>
          </select>
          <p className="text-xs text-white/40 mt-1 font-condensed">Pre-selected on your Upload &amp; FTP Import screens. You can still switch per-batch. Sitewide default is GBP.</p>
        </div>
        <button type="submit" disabled={busy} className="pm-btn" data-testid="my-save">
          {busy ? "Saving…" : "Save profile"}
        </button>
      </form>

      <div className="space-y-5">
        <h3 className="font-display text-3xl uppercase">Payouts</h3>
        <div className="border-2 border-[var(--pm-orange-40)] p-5 bg-[#0a0a0a]">
          <p className="text-xs uppercase tracking-[0.3em] pm-orange font-semibold">Commission rate</p>
          <p className="font-display text-5xl uppercase mt-2">{Math.round((me.commission_rate || 0.15) * 100)}%</p>
          <p className="text-sm text-white/60 font-condensed mt-2">
            {site.siteName}&apos;s platform fee on every sale. You keep {100 - Math.round((me.commission_rate || 0.15) * 100)}%.
          </p>
        </div>

        <div className="border-2 border-white/10 p-5">
          <p className="text-xs uppercase tracking-[0.3em] text-white/60 font-semibold">Stripe payout</p>
          <p className="text-sm text-white/60 font-condensed mt-2">
            Connect your Stripe account and buyer payments split automatically — no manual payouts needed.
          </p>
          <p className="text-xs uppercase tracking-[0.2em] text-yellow-300 mt-4 font-semibold">
            Coming soon — Stripe Connect activation pending. Your earnings are tracked as pending payout in the meantime.
          </p>
        </div>

        <ChangePasswordCard />
      </div>
    </div>
  );
}
